import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useSwipeRight } from '@/hooks/useSwipeRight';

import { isTopOverlay, pushOverlay } from '../Modal/overlayStack';

import styles from './SidePanel.module.scss';

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  zIndex?: number;
  /**
   * Leaves the page behind scrollable while the panel is open. For a panel the reader
   * browses alongside the page — a preview they keep open while moving down a list —
   * where locking the scroll would stop them reaching the next thing they want to open.
   * Leave it off for anything meant to be finished or cancelled, a form or a
   * confirmation, where the page behind should be inert.
   */
  browsable?: boolean;
}

/** Open panels holding the page still. Panels stack, so the lock goes with the last of them. */
let scrollLocks = 0;

const SidePanel = ({ open, onClose, children, zIndex, browsable = false }: SidePanelProps) => {
  const panelRef = useRef<HTMLElement>(null);
  // Keep a ref to onClose so the effects don't need it as a dependency.
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const self = {};
    const removeOverlay = pushOverlay(self);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTopOverlay(self)) onCloseRef.current();
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      removeOverlay();
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (browsable || !open) return;
    scrollLocks += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      scrollLocks -= 1;
      if (!scrollLocks) document.body.style.overflow = '';
    };
  }, [open, browsable]);

  useSwipeRight({
    enabled: open,
    surfaceFor: (target) => (panelRef.current?.contains(target) ? panelRef.current : null),
    onRelease: (panel, passed) => {
      // Back to the CSS transition: closing slides out from where the finger left the panel;
      // staying needs a reflow first so the 220ms snap-back starts from there too.
      panel.style.transition = '';
      if (passed) {
        panel.style.transform = '';
        onCloseRef.current();
        return;
      }
      void panel.offsetWidth;
      panel.style.transform = '';
    },
  });

  return createPortal(
    <div
      className={clsx(styles.sideOverlay, open && styles.sideOverlayOpen)}
      style={zIndex !== undefined ? { zIndex } : undefined}
    >
      <div className={styles.sideBackdrop} onClick={onClose} />
      <aside ref={panelRef} className={clsx(styles.sidePanel, open && styles.sidePanelOpen)}>
        {children}
      </aside>
    </div>,
    document.body,
  );
};

export default SidePanel;
